import { AnimatePresence, motion } from 'framer-motion';
import { Feather, Scale, ScrollText } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { useGetTasksQuery } from '@/services/api/tasksService';
import { CanonicalTaskStatus } from '@/services/types/task-types';
import { selectUser } from '@/state/selectors/root-selectors';

// Ink splatter SVG component
const InkSplatter = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 100 100"
    className={className}
    style={{ filter: 'url(#ink-blur)' }}
  >
    <defs>
      <filter id="ink-blur">
        <feGaussianBlur in="SourceGraphic" stdDeviation="0.5" />
      </filter>
    </defs>
    <circle cx="50" cy="50" r="20" fill="currentColor" opacity="0.8" />
    <circle cx="35" cy="40" r="8" fill="currentColor" opacity="0.6" />
    <circle cx="70" cy="55" r="6" fill="currentColor" opacity="0.5" />
    <circle cx="45" cy="70" r="4" fill="currentColor" opacity="0.4" />
    <circle cx="60" cy="35" r="5" fill="currentColor" opacity="0.5" />
    <ellipse cx="30" cy="60" rx="3" ry="5" fill="currentColor" opacity="0.3" />
    <ellipse cx="75" cy="45" rx="4" ry="2" fill="currentColor" opacity="0.4" />
  </svg>
);

// Wax seal component for final slide
const WaxSeal = ({ className }: { className?: string }) => (
  <div className={cn('relative', className)}>
    <svg viewBox="0 0 100 100" className="w-full h-full">
      <defs>
        <radialGradient id="wax-gradient" cx="40%" cy="40%">
          <stop offset="0%" stopColor="#8B0000" />
          <stop offset="50%" stopColor="#660000" />
          <stop offset="100%" stopColor="#4a0000" />
        </radialGradient>
        <filter id="wax-shadow">
          <feDropShadow dx="2" dy="2" stdDeviation="2" floodOpacity="0.4" />
        </filter>
      </defs>
      <circle
        cx="50"
        cy="50"
        r="45"
        fill="url(#wax-gradient)"
        filter="url(#wax-shadow)"
      />
      <circle
        cx="50"
        cy="50"
        r="35"
        fill="none"
        stroke="#4a0000"
        strokeWidth="2"
        opacity="0.5"
      />
      <text
        x="50"
        y="58"
        textAnchor="middle"
        fill="#ffd700"
        fontSize="24"
        fontFamily="Pinyon Script, cursive"
        opacity="0.9"
      >
        F
      </text>
    </svg>
  </div>
);

// Quill pen decoration
const QuillDecoration = ({ className }: { className?: string }) => (
  <motion.div
    className={cn('absolute pointer-events-none', className)}
    initial={{ opacity: 0, rotate: -20 }}
    animate={{ opacity: 0.6, rotate: 0 }}
    transition={{ delay: 0.5, duration: 0.8 }}
  >
    <Feather className="w-16 h-16 text-amber-900/40" />
  </motion.div>
);

// Month names for display
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const MONTH_SHORT_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

interface WrappedStats {
  totalTasksCompleted: number;
  totalMattersHandled: number;
  topTaskGroup: string;
  topTaskGroupCount: number;
  tasksByGroup: { group: string; count: number }[];
  mostProductiveMonth: string;
  mostProductiveMonthCount: number;
  avgTasksPerDay: number;
  tasksByMonth: { month: string; count: number }[];
}

interface Slide {
  id: string;
  title: string;
  subtitle?: string;
  value?: string | number;
  suffix?: string;
  isChart?: boolean;
  isFinale?: boolean;
}

// Ink reveal animation for text
const InkText = ({
  children,
  delay = 0,
  className,
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => (
  <motion.span
    className={cn('inline-block', className)}
    initial={{ opacity: 0, filter: 'blur(8px)' }}
    animate={{ opacity: 1, filter: 'blur(0px)' }}
    transition={{
      delay,
      duration: 0.8,
      ease: 'easeOut',
    }}
  >
    {children}
  </motion.span>
);

// Ink drip animation
const InkDrip = ({ delay = 0 }: { delay?: number }) => (
  <motion.div
    className="absolute left-1/2 -translate-x-1/2 w-1 bg-gradient-to-b from-amber-950 to-transparent"
    initial={{ height: 0, opacity: 0 }}
    animate={{ height: 40, opacity: [0, 0.6, 0] }}
    transition={{
      delay,
      duration: 1.5,
      ease: 'easeIn',
    }}
  />
);

export const WrappedPage = () => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Get current user from Redux store
  const currentUser = useSelector(selectUser);

  // Fetch completed tasks assigned to the current user
  const { data: tasksData, isLoading: isLoadingTasks } = useGetTasksQuery(
    {
      assignee: currentUser?.id ? [currentUser.id] : [],
      statuses: [CanonicalTaskStatus.COMPLETED],
      limit: 1000,
    },
    {
      skip: !currentUser?.id,
    },
  );

  // Auto-focus container on mount for keyboard navigation
  useEffect(() => {
    const timer = setTimeout(() => {
      containerRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Compute stats from real task data
  const stats: WrappedStats = useMemo(() => {
    const tasks = tasksData?.results || [];
    const currentYear = 2025;

    const completedTasks = tasks.filter((task) => {
      if (task.completed_at) {
        const completedDate = new Date(task.completed_at);
        return completedDate.getFullYear() === currentYear;
      }
      return task.status?.canonical_status === CanonicalTaskStatus.COMPLETED;
    });

    const tasksIn2025 =
      completedTasks.length > 0
        ? completedTasks
        : tasks.filter(
            (t) => t.status?.canonical_status === CanonicalTaskStatus.COMPLETED,
          );

    const groupCounts: Record<string, number> = {};
    tasksIn2025.forEach((task) => {
      const group = task.group?.canonical_group || 'Miscellaneous';
      groupCounts[group] = (groupCounts[group] || 0) + 1;
    });

    const tasksByGroup = Object.entries(groupCounts)
      .map(([group, count]) => ({ group, count }))
      .sort((a, b) => b.count - a.count);

    const topGroup = tasksByGroup[0] || { group: 'None', count: 0 };

    const uniqueMatters = new Set(
      tasksIn2025.map((task) => task.matter).filter(Boolean),
    );

    const monthCounts: Record<number, number> = {};
    tasksIn2025.forEach((task) => {
      if (!task.completed_at) return;
      const month = new Date(task.completed_at).getMonth();
      monthCounts[month] = (monthCounts[month] || 0) + 1;
    });

    const tasksByMonth = MONTH_SHORT_NAMES.map((month, i) => ({
      month,
      count: monthCounts[i] || 0,
    }));

    let mostProductiveMonth = 'None';
    let mostProductiveMonthCount = 0;
    Object.entries(monthCounts).forEach(([monthIndex, count]) => {
      if (count > mostProductiveMonthCount) {
        mostProductiveMonthCount = count;
        mostProductiveMonth = MONTH_NAMES[parseInt(monthIndex, 10)];
      }
    });

    const today = new Date();
    const startOfYear = new Date(currentYear, 0, 1);
    const daysSoFar = Math.ceil(
      (today.getTime() - startOfYear.getTime()) / (1000 * 60 * 60 * 24),
    );
    const workingDays = Math.min(daysSoFar, 260) * (5 / 7);
    const avgTasksPerDay =
      workingDays > 0
        ? Math.round((tasksIn2025.length / workingDays) * 10) / 10
        : 0;

    return {
      totalTasksCompleted: tasksIn2025.length,
      totalMattersHandled: uniqueMatters.size,
      topTaskGroup: topGroup.group,
      topTaskGroupCount: topGroup.count,
      tasksByGroup,
      mostProductiveMonth,
      mostProductiveMonthCount,
      avgTasksPerDay,
      tasksByMonth,
    };
  }, [tasksData]);

  // Generate slides based on stats
  const slides: Slide[] = useMemo(() => {
    const baseSlides: Slide[] = [
      {
        id: 'intro',
        title: 'Anno Domini MMXXV',
        subtitle: 'A Chronicle of Your Legal Endeavors',
      },
      {
        id: 'total-tasks',
        title: 'Tasks Completed',
        value: stats.totalTasksCompleted,
        suffix: 'tasks fulfilled',
        subtitle: 'with diligence and care',
      },
      {
        id: 'matters',
        title: 'Matters Attended',
        value: stats.totalMattersHandled,
        suffix: 'cases served',
        subtitle: 'in pursuit of justice',
      },
    ];

    if (stats.topTaskGroupCount > 0) {
      baseSlides.push({
        id: 'top-task',
        title: 'Your Expertise',
        value: stats.topTaskGroup,
        subtitle: `${stats.topTaskGroupCount} tasks of this nature`,
      });
    }

    if (stats.mostProductiveMonthCount > 0) {
      baseSlides.push({
        id: 'productive-month',
        title: 'Most Industrious Month',
        value: stats.mostProductiveMonth,
        subtitle: `${stats.mostProductiveMonthCount} tasks accomplished`,
      });
    }

    if (stats.avgTasksPerDay > 0) {
      baseSlides.push({
        id: 'daily-avg',
        title: 'Daily Constitution',
        value: stats.avgTasksPerDay,
        suffix: 'tasks per diem',
        subtitle: 'a steady hand prevails',
      });
    }

    if (stats.tasksByGroup.length > 0) {
      baseSlides.push({
        id: 'breakdown',
        title: 'The Ledger',
        subtitle: 'A record of your labors',
        isChart: true,
      });
    }

    baseSlides.push({
      id: 'finale',
      title: 'Witnessed & Sealed',
      subtitle: `In the Year of Our Lord, ${new Date().getFullYear()}`,
      isFinale: true,
    });

    return baseSlides;
  }, [stats]);

  const handleNext = useCallback(() => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide((prev) => prev + 1);
    } else {
      navigate('/');
    }
  }, [currentSlide, slides.length, navigate]);

  const handlePrev = useCallback(() => {
    if (currentSlide > 0) {
      setCurrentSlide((prev) => prev - 1);
    }
  }, [currentSlide]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrev();
      } else if (e.key === 'Escape') {
        navigate(-1);
      }
    },
    [handleNext, handlePrev, navigate],
  );

  // Loading state with parchment theme
  if (isLoadingTasks) {
    return (
      <div
        className="fixed inset-0 flex items-center justify-center"
        style={{
          background:
            'linear-gradient(135deg, #f4e4bc 0%, #e8d5b0 50%, #d4c4a0 100%)',
        }}
      >
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
          >
            <ScrollText className="w-12 h-12 text-amber-900/60 mx-auto mb-4" />
          </motion.div>
          <p
            className="text-amber-900/60 text-lg"
            style={{ fontFamily: "'IM Fell English', serif" }}
          >
            Unfurling the scroll...
          </p>
        </div>
      </div>
    );
  }

  const slide = slides[currentSlide];
  const maxCount = Math.max(...stats.tasksByGroup.map((g) => g.count), 1);

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 overflow-hidden cursor-pointer focus:outline-none"
      onClick={handleNext}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label="Next slide"
      style={{
        background:
          'linear-gradient(135deg, #f4e4bc 0%, #e8d5b0 50%, #d4c4a0 100%)',
      }}
    >
      {/* Parchment texture overlay */}
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Aged paper edges effect */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          boxShadow: 'inset 0 0 100px rgba(139, 69, 19, 0.3)',
        }}
      />

      {/* Decorative corner flourishes */}
      <div className="absolute top-8 left-8 w-24 h-24 border-t-2 border-l-2 border-amber-900/20 rounded-tl-lg" />
      <div className="absolute top-8 right-8 w-24 h-24 border-t-2 border-r-2 border-amber-900/20 rounded-tr-lg" />
      <div className="absolute bottom-8 left-8 w-24 h-24 border-b-2 border-l-2 border-amber-900/20 rounded-bl-lg" />
      <div className="absolute bottom-8 right-8 w-24 h-24 border-b-2 border-r-2 border-amber-900/20 rounded-br-lg" />

      {/* Quill decorations */}
      <QuillDecoration className="top-20 right-20 rotate-45" />
      <QuillDecoration className="bottom-20 left-20 -rotate-45 scale-x-[-1]" />

      {/* Close button */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          navigate(-1);
        }}
        className="absolute top-6 right-6 text-amber-900/40 hover:text-amber-900/70 text-sm z-50 transition-colors"
        style={{ fontFamily: "'IM Fell English', serif" }}
      >
        Press ESC to close
      </button>

      {/* Progress dots styled as ink drops */}
      <div className="absolute top-6 left-1/2 -translate-x-1/2 flex gap-3 z-50">
        {slides.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setCurrentSlide(i);
            }}
            className={cn(
              'w-2 h-2 rounded-full transition-all duration-300',
              i === currentSlide
                ? 'bg-amber-900 w-6'
                : i < currentSlide
                  ? 'bg-amber-900/60'
                  : 'bg-amber-900/20',
            )}
          />
        ))}
      </div>

      {/* Navigation hint */}
      <div
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-amber-900/40 text-sm z-50"
        style={{ fontFamily: "'IM Fell English', serif" }}
      >
        {currentSlide < slides.length - 1
          ? 'Click or press → to continue'
          : 'Click to return'}
      </div>

      {/* Main content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
          className="absolute inset-0 flex flex-col items-center justify-center px-8"
        >
          {/* Ink splatter decoration */}
          <motion.div
            className="absolute top-1/4 left-1/4 w-32 h-32 text-amber-900/10"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <InkSplatter />
          </motion.div>

          {/* Icon for intro */}
          {slide?.id === 'intro' && (
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="mb-8"
            >
              <Scale className="w-16 h-16 text-amber-900/70" />
            </motion.div>
          )}

          {/* Title */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-amber-950 text-2xl md:text-4xl mb-6 text-center"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontWeight: 600,
              letterSpacing: '0.05em',
            }}
          >
            <InkText delay={0.3}>{slide?.title}</InkText>
          </motion.h2>

          {/* Decorative line */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="w-32 h-px bg-gradient-to-r from-transparent via-amber-900/40 to-transparent mb-8"
          />

          {/* Value - Large script number */}
          {slide?.value !== undefined && !slide.isChart && !slide.isFinale && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="text-center relative"
            >
              <InkDrip delay={0.6} />
              <span
                className="text-7xl md:text-9xl text-amber-950 block"
                style={{
                  fontFamily: "'Pinyon Script', cursive",
                  textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
                }}
              >
                <InkText delay={0.5}>
                  {typeof slide.value === 'number'
                    ? slide.value.toLocaleString()
                    : slide.value}
                </InkText>
              </span>
              {slide.suffix && (
                <span
                  className="text-amber-900/70 text-xl md:text-2xl mt-2 block"
                  style={{ fontFamily: "'IM Fell English', serif" }}
                >
                  <InkText delay={0.7}>{slide.suffix}</InkText>
                </span>
              )}
            </motion.div>
          )}

          {/* Chart for breakdown slide - styled as a ledger */}
          {slide?.isChart && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="w-full max-w-xl space-y-3 mt-4 px-8 py-6 rounded-lg"
              style={{
                background: 'rgba(255, 255, 255, 0.3)',
                boxShadow: 'inset 0 0 20px rgba(139, 69, 19, 0.1)',
              }}
            >
              {stats.tasksByGroup.slice(0, 6).map((group, i) => (
                <motion.div
                  key={group.group}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + i * 0.1, duration: 0.4 }}
                  className="flex items-center gap-4"
                >
                  <span
                    className="text-amber-900/80 text-sm w-36 text-right truncate"
                    style={{ fontFamily: "'IM Fell English', serif" }}
                  >
                    {group.group}
                  </span>
                  <div className="flex-1 h-6 bg-amber-900/10 rounded overflow-hidden border border-amber-900/20">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${(group.count / maxCount) * 100}%` }}
                      transition={{
                        delay: 0.6 + i * 0.1,
                        duration: 0.8,
                        ease: 'easeOut',
                      }}
                      className="h-full rounded bg-gradient-to-r from-amber-800 to-amber-900"
                    />
                  </div>
                  <span
                    className="text-amber-950 font-semibold w-12 text-right"
                    style={{ fontFamily: "'Pinyon Script', cursive" }}
                  >
                    {group.count}
                  </span>
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* Finale slide with wax seal */}
          {slide?.isFinale && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="flex flex-col items-center"
            >
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.7, type: 'spring', stiffness: 100 }}
              >
                <WaxSeal className="w-24 h-24 mb-6" />
              </motion.div>
              <p
                className="text-amber-950 text-5xl md:text-7xl mb-4"
                style={{ fontFamily: "'Pinyon Script', cursive" }}
              >
                <InkText delay={0.9}>{stats.totalTasksCompleted}</InkText>
              </p>
              <p
                className="text-amber-900/70 text-lg"
                style={{ fontFamily: "'IM Fell English', serif" }}
              >
                <InkText delay={1.1}>Tasks Completed</InkText>
              </p>
            </motion.div>
          )}

          {/* Subtitle */}
          {slide?.subtitle && !slide.isFinale && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="text-amber-900/60 text-lg md:text-xl mt-6 text-center italic"
              style={{ fontFamily: "'IM Fell English', serif" }}
            >
              {slide.subtitle}
            </motion.p>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Navigation buttons styled as old arrows */}
      {currentSlide > 0 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handlePrev();
          }}
          className="absolute left-6 top-1/2 -translate-y-1/2 text-amber-900/30 hover:text-amber-900/60 text-4xl z-50 transition-colors"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          ‹
        </button>
      )}
      {currentSlide < slides.length - 1 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleNext();
          }}
          className="absolute right-6 top-1/2 -translate-y-1/2 text-amber-900/30 hover:text-amber-900/60 text-4xl z-50 transition-colors"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          ›
        </button>
      )}

      {/* Final slide - done button */}
      {currentSlide === slides.length - 1 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3 }}
          className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-4 z-50"
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigate('/');
            }}
            className="px-8 py-3 rounded border-2 border-amber-900/40 text-amber-900 hover:bg-amber-900/10 transition-colors"
            style={{
              fontFamily: "'IM Fell English', serif",
              letterSpacing: '0.1em',
            }}
          >
            Return to Chambers
          </button>
        </motion.div>
      )}
    </div>
  );
};
